import { _toXY, checkCollinear, getDistance, moveTo } from './math'

export function genPiePath (startAngle, endAngle, radiusIn, radius, { maxX, maxY, padding }) {
    const [x, y] = [maxX / 2, maxY / 2]
    const startIn = _toXY(x, y, radius, endAngle - 1)
    const endIn = _toXY(x, y, radius, startAngle)
    const startOut = _toXY(x, y, radiusIn, endAngle - 1)
    const endOut = _toXY(x, y, radiusIn, startAngle)
    const arcSweep = (endAngle - startAngle) <= 180 ? '0' : '1'
    const pathData = [
        'M', startIn.x, startIn.y,
        'L', startOut.x, startOut.y,
        'A', radiusIn, radiusIn, 0, arcSweep, 0, endOut.x, endOut.y,
        'L', endIn.x, endIn.y,
        'A', radius, radius, 0, arcSweep, 1, startIn.x, startIn.y,
        'z'
    ]
    const labelAngle = (startAngle + endAngle) / 2
    return {
        d: pathData.join(' '),
        x: _toXY(x, y, radius * 0.8, labelAngle).x + (startAngle > 180 ? padding * -3 : startAngle > 90 ? padding * -2 : padding),
        y: _toXY(x, y, radius * 0.8, labelAngle).y + (startAngle > 180 ? padding : startAngle > 0 ? padding * 2 : padding)
    }
}

export function genPieStrokePath (startAngle, endAngle, radiusIn, radiusOut) {
    const x = 250
    const y = 250
    const startIn = _toXY(x, y, radiusIn, startAngle)
    const endIn = _toXY(x, y, radiusIn, endAngle)
    const arcSweep = (endAngle - startAngle) <= 180 ? '0' : '1'
    const pathData = [
        'M', startIn.x, startIn.y,
        'A', radiusIn, radiusIn, 0, arcSweep, 1, endIn.x, endIn.y
    ].join(' ')
    return pathData
}
/**
 *  Calculate the coordinate
 * @param  {numver}             grid
 * @param  {object}             boundary
 * @param  {object}             value
 * @param  {number[]|object[]}  arr
 * @param  {String}             yKey
 * @return {object[]}
 */
export function genPoints ({ yGrid }, { minX, minY, maxX, maxY, padding }, { max, min }, arr, yKey) {
    if (max === min && min) {
        max += 1
        min -= 1
    }
    const span = max - min
    const yMagic = Math.floor(Math.log(span) / Math.log(10))
    const unit = Math.pow(10, yMagic)

    let gridMin = Math.floor(min / unit) * unit
    let gridMax = Math.ceil(max / unit) * unit
    let step = (gridMax - gridMin) / (yGrid - 1)
    if (unit === 1) {
        step = Math.ceil(step)
        gridMax = gridMin + step * (yGrid - 1)
    }

    if (gridMin < 0 && gridMax > 0) {
        gridMin = isNaN(gridMin) ? (min / unit) * unit : gridMin
        gridMax = isNaN(gridMax) ? (max / unit) * unit : gridMax
    }
    if (min === 0 && max === 0) {
        gridMin = 0
        gridMax = 1
        step = 1 / (yGrid - 1)
    }
    const values = arr.map(item => (typeof item === 'number' ? item : item[yKey]))

    let minValue = Math.min(...values, gridMin)
    minValue = isNaN(minValue) ? 0 : minValue

    const len = arr.length - 1
    const gridX = (maxX - minX - padding) / len
    const gridY = (maxY - minY) / (Math.max(...values, gridMax) - minValue)

    const result = arr.map((item, index) => {
        const value = item[yKey]
        if (value !== null) {
            return {
                x: arr.length === 1 ? (maxX - minX - padding) / 2 : index * gridX + minX,
                y:
                    maxY -
                    (value - minValue) * gridY +
                    +(index === len) * 0.00001 -
                    +(index === 0) * 0.00001
            }
        } else {
            return undefined
        }
    })
    return result
}

/**
 * From https://github.com/unsplash/react-trend/blob/master/src/helpers/DOM.helpers.js#L18
 */
export function genPath (points, radius, height) {
    let start = points.shift()
    while (!start) {
        start = points.shift()
    }
    const pathData = []
    const areaData = []
    let endData = ''
    if (!start) return
    pathData.push(`M${start.x}, ${start.y}`)
    areaData.push(`M${start.x}, ${start.y}`)
    points.map((point, index) => {
        if (!point) {
            if (index !== 0 && points[index - 1]) areaData.push(`L ${points[index - 1].x}, ${height} L ${start.x}, ${height} z`)
            return
        }
        if (!points[index - 1]) {
            if (index === 0) {
                pathData.push(`L${point.x} ${point.y}`)
                areaData.push(`L${point.x} ${point.y}`)
            } else {
                start = point
                pathData.push(`M${point.x} ${point.y}`)
                areaData.push(`M${point.x} ${point.y}`)
            }
            return
        }

        const prev = points[index - 1] || point
        const next = points[index + 1]
        const isCollinear = next && checkCollinear(next, point, prev)

        if (!next || isCollinear) {
            pathData.push(`L${point.x} ${point.y}`)
            areaData.push(`L${point.x} ${point.y}`)
            if (points[points.length - 1]) {
                endData = `L ${point.x}, ${height} L ${start.x}, ${height} z`
            }
            return
        }
        const threshold = Math.min(
            getDistance(prev, point),
            getDistance(next, point)
        )
        const isTooCloseForRadius = threshold / 2 < radius
        const radiusForPoint = isTooCloseForRadius ? threshold / 2 : radius
        const before = moveTo(prev, point, radiusForPoint)
        const after = moveTo(next, point, radiusForPoint)
        pathData.push(`L${before.x} ${before.y}S${point.x} ${point.y} ${after.x} ${after.y}`)
        areaData.push(`L${before.x} ${before.y}S${point.x} ${point.y} ${after.x} ${after.y}`)
    })
    areaData.push(endData)
    return {
        linePath: pathData.join(''),
        areaPath: areaData.join('')
    }
}
